import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { ownerKey, useOwnerId } from "../features/auth/owner.ts";
import { api, query, type Schemas } from "./client.ts";
import { NOTIFICATIONS_KEY } from "./notifications.ts";

/**
 * Las notas de la bitacora: lo que la plantilla llama "tweets". Un pensamiento
 * del dia sin operacion asociada.
 *
 * Comparten linea de tiempo con las operaciones, asi que se piden con la misma
 * forma de cursor y se mezclan por dia en el cliente.
 */

export type Note = Schemas["NoteDto"];
export type NoteList = Schemas["NoteListResponse"];

const keys = {
  notes: ["notes"] as const,
};

/** La misma paginacion por cursor que las operaciones, para poder mezclarlas. */
export function useNotes(enabled = true) {
  const owner = useOwnerId();

  return useInfiniteQuery({
    /* El dueno detras del prefijo (ver `api/hooks.ts`); las mutaciones
       invalidan por `keys.notes`, que sigue siendo el prefijo. */
    queryKey: [...keys.notes, ownerKey(owner)] as const,
    queryFn: ({ pageParam }) =>
      api.get<NoteList>(`/api/v1/notes${query({ cursor: pageParam, pageSize: 50, owner })}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: NoteList) => last.nextCursor ?? undefined,
    enabled,
  });
}

function useInvalidateNotes(): () => void {
  const client = useQueryClient();

  return () => {
    void client.invalidateQueries({ queryKey: keys.notes });
    /* Una nota tambien cuenta como registrar el dia, asi que apaga el aviso de
       constancia igual que una operacion. */
    void client.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  };
}

export function useCreateNote() {
  const invalidate = useInvalidateNotes();

  return useMutation({
    mutationFn: (body: unknown) => api.post<Schemas["CreatedResponse"]>("/api/v1/notes", body),
    onSuccess: invalidate,
  });
}

export function useUpdateNote(id: string) {
  const invalidate = useInvalidateNotes();

  return useMutation({
    mutationFn: (body: unknown) => api.put<void>(`/api/v1/notes/${id}`, body),
    onSuccess: invalidate,
  });
}

export function useDeleteNote() {
  const invalidate = useInvalidateNotes();

  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/v1/notes/${id}`),
    onSuccess: invalidate,
  });
}
